<template>
    <div v-if="content" class="s-form-success" :class="className">
        <a-glyph v-if="content.glyph" fill="fixed-success" :name="content.glyph" />
        <p v-if="content.header" class="header" v-html="content.header" />
        <p v-if="content.paragraph" class="paragraph" v-html="content.paragraph" />
        <a-picture
            v-if="content.image"
            class="image"
            :link="content.image"
            :alt="content.imageAlt || content.title || content.header"
        />

        <template v-if="content.info">
            <a-dangerous-html class="info" :content="info" />
        </template>

        <p v-if="content.title && !content.header" class="title">
            {{ content.title }}
        </p>

        <template v-if="content.description">
            <a-dangerous-html class="description" :content="description" />
        </template>

        <a-link
            v-if="content.backToLink"
            v-bind="content.backToLink"
            :class="content.backToLink.className"
            @click="$emit('backToForm', content.backToLink.backTo)"
        />

        <template v-if="content.button">
            <a-button
                v-if="content.button.to"
                class="cta-button"
                :class="content.button.className"
                v-bind="omitClassName(content.button)"
                :type="content.button.type || 'action'"
                :text="content.button.text || 'Download'"
                :event="content.button.event || { doNotSendGA: true }"
            />
            <a-button
                v-else
                class="cta-button"
                :class="content.button.className"
                v-bind="omitClassName(content.button)"
                :type="content.button.type || 'action'"
                :text="content.button.text || 'Download'"
                :event="content.button.event || { doNotSendGA: true }"
                @click="$emit('onClickButtonSuccess')"
            />
        </template>

        <div v-if="content.links?.length" class="links">
            <dynamic-link
                v-for="(link, i) in content.links"
                :key="i"
                class="link"
                v-bind="link"
            >
                {{ link.text }}
            </dynamic-link>
        </div>

        <div v-if="content.downloadButtons?.length" ref="downloadButtons" class="download-buttons">
            <a-button
                v-for="button in content.downloadButtons"
                :key="button.to"
                class="button"
                :class="button.className"
                v-bind="omitClassName(button)"
                :type="button.type || 'action'"
                :event="button.event || { doNotSendGA: true }"
                :data-platform="button.platform"
            />
        </div>
    </div>
</template>

<script>
import AButton from '@core/components/button/button.vue';
import ADangerousHtml from '@core/components/dangerous-html/dangerous-html.vue';
import DynamicLink from '@core/components/dynamic-link/dynamic-link.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import ALink from '@core/components/link/link.vue';
import APicture from '@core/components/picture/picture.vue';
import form from '@core/mixins/form';
import commonUtils from '@utils/common';
import formHelper from '@utils/form.js';

export default {
    name: 'FormSuccess',

    components: {
        ADangerousHtml,
        DynamicLink,
        AButton,
        AGlyph,
        ALink,
        APicture,
    },

    mixins: [form],

    props: {
        userData: {
            type: Object,
            default: null,
        },
        form: {
            type: Object,
            default: () => ({
                image: '/images/partner-form/success',
                description: 'Your submission has been sent',
                title: 'Thank you',
            }),
        },
    },

    emits: ['onClickButtonSuccess', 'backToForm'],

    data: () => ({
        content: {},
    }),

    computed: {
        className() {
            return {
                'with-downloads': Boolean(this.content.downloadButtons?.length),
                'small-paddings': Boolean(this.content.header && this.content.links),
            };
        },
        info() {
            const text = this.content.info || '';
            return this.replaceText(text, 'first_name');
        },
        description() {
            const text = this.content.description || '';
            return this.replaceText(text, 'email');
        },
    },
    beforeMount() {
        const variation = this.form?.variation?.field;
        const value = this.userData?.[variation];
        const hasVariation = Boolean(value && this.form?.variation?.value?.[value]);
        this.content = hasVariation ? this.form.variation.value[value] : this.form;
        this.content.eventTracking = this.form.eventTracking || false;
    },
    async mounted() {
        if (this.content.isPromoSubscription) {
            const companyType = this.userData.company_type || '';
            const product = {
                'Corporate End-Customer': 'Business news and products',
                'Prosumer End-Customer': 'Home and Office news and products',
                'Service Provider': 'Service Provider news and products',
            };

            this.sendDataLayer({
                eventLabel: `news letter subscription: ${product[companyType] || ''}`,
                eventAction: 'submit_form_success',
                eventCategory: 'form',
                formTimer: formHelper.getSecondsOnPage(), // WEB-44946
                emailAddress: await commonUtils.toSHA256String(this.userData.email), // WEB-45304
            });
            return;
        }

        if (this.content.eventTracking) {
            this.sendDataLayer({
                eventAction: window.location.href,
                ...this.content.eventTracking,
                formTimer: formHelper.getSecondsOnPage(), // WEB-44946
                emailAddress: await commonUtils.toSHA256String(this.userData.email), // WEB-45304
            });
        }

        if (this.content.downloadButtons) {
            const selectedOS = /mac|iphone|ipad|ipod/i.test(navigator.userAgent) ? 'MAC' : 'PC';
            this.$refs.downloadButtons.querySelector(`[data-platform="${selectedOS}"]`)?.click();
        }
    },

    methods: {
        omitClassName(obj) {
            const props = { ...obj };
            delete props.className;
            return props;
        },
        replaceText(text, key) {
            const value = this.userData?.[key] || '';
            return text.replace(`@${key}`, value);
        },
    },
};
</script>

<style lang="pcss" scoped>
.s-form-success {
    width: 100%;
    display: flex;
    text-align: center;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    border-radius: 4px;
    padding: 89px 24px 90px;
    color: var(--av-nav-primary);
    background: var(--av-inversed-primary);
    border: 1px solid var(--av-fixed-lightest);
    box-shadow: 0 4px 8px rgba(36, 49, 67, 0.2);

    @media (--viewport-desktop-large) {
        padding: 113px 24px 114px;
    }
    @media (--viewport-desktop-wide) {
        padding: 105px 24px 106px;
    }

    .info {
        @mixin body-accent;
        margin-top: 8px;
    }

    .title {
        @mixin title;
        font-weight: 500;
        margin: 32px 0 0;
        color: var(--av-nav-primary);
    }

    .image {
        height: 250px;
        margin: 24px 0 auto;

        @media (--viewport-desktop) {
            height: 252px;
        }
        @media (--viewport-desktop-wide) {
            height: 250px;
        }

        :deep(.a-picture__img) {
            height: 100%;
        }
    }

    .link,
    .paragraph,
    .description {
        @mixin paragraph;
        margin: 24px 0 0;
        max-width: 472px;
    }

    .header {
        @mixin title-accent;
        max-width: 472px;
    }

    :deep(a) {
        text-decoration: none;
        color: var(--av-brand-secondary);
    }

    .a-button.cta-button {
        color: var(--av-inversed-primary);
    }

    .links {
        display: flex;
        flex-direction: column;
    }

    .cta-button {
        margin-top: 24px;
    }

    &.small-paddings {
        padding: 16px 40px 40px;

        .header {
            margin-bottom: 24px;
        }

        .cta-button.full-width {
            width: 100%;
        }
    }

    &.with-downloads {
        padding: 24px 32px 32px;

        .header {
            @mixin lead-accent;
            margin-top: 8px;
        }
    }

    .extra-button {
        margin: 16px 0 0;

        &.a-button_type_inverse {
            border-radius: 4px;
            border: 1px solid var(--av-brand-primary);

            &:hover {
                background: var(--av-brand-accent);
            }

            &:active {
                background: var(--el-secondary-active);
            }

            :deep(.a-button__content) {
                color: var(--av-brand-primary);
                @mixin paragraph-accent;
            }
        }
    }

    .download-buttons {
        margin: 16px 0 0;
        display: flex;
        flex-wrap: wrap;
        flex-direction: row;
        gap: 16px;

        .a-button {
            width: 100%;
            border-color: var(--av-brand-primary);
            color: var(--av-brand-primary);

            @media (--viewport-tablet) {
                flex: 1;
                width: 50%;
            }

            :deep(.a-glyph) {
                width: 16px;
                height: 16px;
                fill: var(--av-brand-primary);
            }
        }
    }

    .back-to-form-link {
        margin-top: 16px;

        :deep(.a-link__content) {
            @mixin paragraph;
            color: var(--av-brand-primary);
        }
    }
}
</style>
